import { useState } from 'react';
import {
  Avatar,
  DeleteButton,
  Empty,
  ErrorBanner,
  Field,
  Loading,
  ScaleInput,
} from '../../components/ui';
import { api } from '../../lib/api';
import { formatDate, formatNumber, humanise, todayLocal } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type {
  ApprovedTrainingRequest,
  Employee,
  EmployeeTraining,
  PeopleReview,
  PeopleTemplate,
  PersonDetail,
  ProfileField,
  ReviewAnswer,
  ReviewSummaryRow,
  SelfReviewRating,
  SelfReviewSummaryRow,
} from '../../lib/types';
import { Tabs } from '../am/amUi';
import { PersonActivity } from './PersonActivity';
import { SelfReviewDialog } from './SelfReview';

/**
 * One person, opened.
 *
 * Four tabs: the three things this module knows about somebody — their
 * reviews, their training, and the profile, who they are beyond the org
 * chart — and their Activity, their last year in Autotask.
 *
 * Name, department, role and manager are NOT editable here. They are
 * `core.Employee` columns, and the Employees page is the one place `core` is
 * written (docs/shared-database.md, rule 1). Showing them read-only with a
 * link is the honest version of that rule.
 */

type Tab = 'reviews' | 'training' | 'profile' | 'activity';

interface ProfileFieldSpec {
  key: ProfileField;
  label: string;
  hint?: string;
  type?: 'textarea' | 'select' | 'date';
  options?: string[];
}

/** The CMIT employee profile, carried across intact — minus the four fields
 *  that were duplicating `core.Employee`. See migration 032. */
const PROFILE_SECTIONS: { title: string; fields: ProfileFieldSpec[] }[] = [
  {
    title: 'Basics',
    fields: [
      { key: 'PreferredName', label: 'Preferred name / nickname' },
      { key: 'Location', label: 'Location', hint: 'e.g. Denver, Boulder, PA, Virginia' },
      /* The only field on this profile another module reads: the Admin module's
         birthday board. Optional, and it stays optional — somebody who would
         rather not say is not an incomplete record. The board shows the day and
         month only; the year never leaves the server. */
      {
        key: 'BirthDate',
        label: 'Date of birth',
        type: 'date',
        hint: 'Optional. Shown on the Admin birthday board without the year.',
      },
    ],
  },
  {
    title: 'Personal life',
    fields: [
      { key: 'Hometown', label: 'Hometown / where they’re from' },
      { key: 'SpousePartner', label: 'Spouse / partner name' },
      { key: 'Kids', label: 'Kids — names and ages', hint: 'e.g. Emma (8), Liam (5)' },
      { key: 'Pets', label: 'Pets' },
      { key: 'LivingSituation', label: 'Living situation', hint: 'Owns / rents / relocated recently' },
      { key: 'RecentLifeEvent', label: 'Recent major life event', hint: 'New home, baby, wedding, loss?' },
      { key: 'UpcomingLifeEvent', label: 'Upcoming life event' },
    ],
  },
  {
    title: 'Interests and personality',
    fields: [
      { key: 'Hobbies', label: 'Top hobbies / passions' },
      { key: 'SportsTeams', label: 'Sports teams / fan of' },
      { key: 'VacationType', label: 'Favourite vacation type', hint: 'Beach / mountains / city / adventure' },
      { key: 'DiscProfile', label: 'DISC profile', hint: 'Di / Sc / Cd / IS?' },
      { key: 'CommunicationStyle', label: 'Communication style', hint: 'Direct / collaborative / needs context?' },
      { key: 'EnergizedBy', label: 'What energises them' },
      { key: 'DrainedBy', label: 'What drains them' },
    ],
  },
  {
    title: 'Career and goals',
    fields: [
      { key: 'Goal1Yr', label: 'Short-term career goal (1 yr)', type: 'textarea' },
      { key: 'Goal3To5Yr', label: 'Long-term career goal (3–5 yr)', type: 'textarea' },
      { key: 'SkillsToDevelop', label: 'Skills they want to develop', type: 'textarea' },
      { key: 'MostProudOf', label: 'What they’re most proud of', type: 'textarea' },
      {
        key: 'PrimaryMotivator',
        label: 'Primary motivator',
        hint: 'Income / mastery / recognition / stability / impact',
      },
      { key: 'RecognitionPreference', label: 'Recognition preference', hint: 'Public / private / written / verbal' },
      { key: 'BiggestFrustration', label: 'Biggest frustration at work', type: 'textarea' },
    ],
  },
  {
    title: 'Relationship notes',
    fields: [
      { key: 'HowWeMet', label: 'How we met / first impression' },
      { key: 'TrustLevel', label: 'Trust level', type: 'select', options: ['', 'Building', 'Solid', 'Deep'] },
      { key: 'SensitiveNotes', label: 'Anything sensitive to be aware of' },
      { key: 'OneOnOneStyle', label: 'Preferred 1:1 style', hint: 'Structured agenda / casual / check-in' },
      { key: 'LastMeaningfulConvo', label: 'Last meaningful conversation' },
    ],
  },
  {
    title: 'Notes',
    fields: [
      {
        key: 'Notes',
        label: 'General notes',
        type: 'textarea',
        hint: 'Anything else worth remembering about this person…',
      },
    ],
  },
];

const ALL_PROFILE_FIELDS = PROFILE_SECTIONS.flatMap((s) => s.fields);

export function PersonWorkspace({
  employeeId,
  onBack,
  onChanged,
}: {
  employeeId: number;
  onBack: () => void;
  onChanged: () => void;
}) {
  const person = useResource<PersonDetail>(`/people/directory/${employeeId}`);
  const templates = useResource<PeopleTemplate[]>('/people/templates');
  const reviews = useResource<ReviewSummaryRow[]>(`/people/reviews/employee/${employeeId}`);
  /* From their Autotask Resource, read live — "—" when they are not in
     Autotask, it has no hire date, or Autotask cannot be reached. */
  const hire = useResource<{ found: boolean; hireDate: string | null }>(`/people/directory/${employeeId}/hire-date`);
  const [tab, setTab] = useState<Tab>('reviews');
  /* An earlier review, built from a People Template, opened from the Reviews
     tab. New reviews are written on the Employees page (migration 173). */
  const [openReviewId, setOpenReviewId] = useState<number | null>(null);
  const employees = useResource<Employee[]>('/employees?status=active');

  if (person.loading) return <Loading label="Loading" />;
  if (person.error) return <ErrorBanner message={person.error} />;
  const p = person.data;
  if (!p) return <ErrorBanner message="That person is not in the directory." />;

  const template = (templates.data ?? []).find((t) => t.TemplateId === p.TemplateId) ?? null;
  const reviewsChanged = () => {
    reviews.reload();
    person.reload();
    onChanged();
  };

  return (
    <>
      <button type="button" className="btn btn-ghost btn-sm mb-4" onClick={onBack}>
        ← All people
      </button>

      <section className="card mb-4">
        <div className="card-head">
          <div className="grow row">
            <Avatar name={p.FullName} />
            <div>
              <h2>{p.FullName}</h2>
              <div className="sub">
                {[p.RoleTitle, p.DepartmentName].filter(Boolean).join(' · ') || 'No role recorded'}
              </div>
            </div>
          </div>
          {p.Status !== 'active' && <span className="badge badge-warning">{humanise(p.Status)}</span>}
        </div>
        <div className="card-body">
          <div className="facts">
            <Fact label="Email" value={p.Email ?? '—'} />
            <Fact label="Manager" value={p.ManagerName ?? '—'} />
            <Fact label="Started" value={formatDate(p.StartDate)} />
            <Fact label="Last review" value={formatDate(p.LastReviewDate)} />
            {/* A fact, not a picker, and that is the change. You used to choose
                a People Template for somebody independently of the job title
                they held in the directory, so an Account Manager could sit here
                with the Systems Engineer template driving their training and
                nothing said a word. The template follows the role now, so there
                is one answer to "what does this person's job expect of them",
                and it is changed where the job is — on the Employees page. */}
            <Fact
              label="People Template"
              value={
                template?.Name ??
                (p.RoleTitle ? `No template for ${p.RoleTitle}` : 'No role recorded')
              }
            />
          </div>

          <p className="xs subtle mt-4">
            The template follows this person's role, which — with their name, department and manager
            — comes from the shared directory and is changed on the Employees page. This module never
            writes to it.
          </p>
        </div>
      </section>

      {template?.JobDescription && (
        <section className="card mb-4">
          <div className="card-head">
            <div className="grow">
              <span className="eyebrow">Job description</span>
              <h3>{template.Name}</h3>
            </div>
          </div>
          <div className="card-body">
            <p className="small" style={{ whiteSpace: 'pre-wrap' }}>
              {template.JobDescription}
            </p>
          </div>
        </section>
      )}

      <Tabs<Tab>
        tabs={[
          { key: 'reviews', label: 'Reviews' },
          { key: 'training', label: 'Training' },
          { key: 'profile', label: 'Profile' },
          /* Their last year in Autotask, in one view — PersonActivity.tsx.
             Their manager's tab, and an admin's: p.CanSeeActivity is the
             server's answer, and routes/peopleActivity.ts answers the same
             question again for the figures themselves. */
          ...(p.CanSeeActivity ? ([{ key: 'activity', label: 'Activity' }] as const) : []),
        ]}
        active={tab}
        onChange={(next) => {
          setOpenReviewId(null);
          setTab(next);
        }}
      />

      {openReviewId !== null ? (
        <ReviewEditor
          reviewId={openReviewId}
          firstName={p.FirstName}
          employees={employees.data ?? []}
          onShareChanged={reviewsChanged}
          onBack={() => {
            setOpenReviewId(null);
            reviewsChanged();
          }}
        />
      ) : (
        <>
          {tab === 'reviews' && (
            <ReviewsTab
              employeeId={employeeId}
              person={p}
              reviews={reviews}
              employees={employees.data ?? []}
              onOpen={setOpenReviewId}
              onChanged={reviewsChanged}
            />
          )}
          {tab === 'training' && (
            <TrainingTab
              key={p.TemplateId ?? 'none'}
              employeeId={employeeId}
              hasRoleTraining={p.TemplateId !== null}
            />
          )}
          {tab === 'profile' && (
            <ProfileTab
              person={p}
              hireDate={hire.loading ? '…' : formatDate(hire.data?.hireDate)}
              onSaved={person.reload}
            />
          )}
          {tab === 'activity' && p.CanSeeActivity && <PersonActivity employeeId={employeeId} />}
        </>
      )}

    </>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="xs muted">{label}</div>
      <div className="strong">{value}</div>
    </div>
  );
}

/* =========================================================================
   Reviews
   ========================================================================= */

const SELF_RATING_LABELS: Record<SelfReviewRating, string> = {
  exceeds: 'Exceeds Expectations',
  meets: 'Meets Expectations',
  needs_improvement: 'Needs Improvement',
  unacceptable: 'Unacceptable',
};

/**
 * A person's reviews, as their People record holds them.
 *
 * MANAGER REVIEWS are written on the Employees page, on the Manager
 * Assessment Form, and appear here only once they are shared — read-only,
 * because this is the record, not the place to edit it (migration 173).
 * SELF REVIEWS are started here. EARLIER REVIEWS are the ones built from
 * People Templates before that, kept as history.
 */
function ReviewsTab({
  employeeId,
  person,
  reviews,
  employees,
  onOpen,
  onChanged,
}: {
  employeeId: number;
  person: PersonDetail;
  reviews: ReturnType<typeof useResource<ReviewSummaryRow[]>>;
  employees: Employee[];
  onOpen: (reviewId: number) => void;
  onChanged: () => void;
}) {
  /* 'new' for a blank form, an id to reopen one, null when closed. */
  const [selfOpen, setSelfOpen] = useState<number | 'new' | null>(null);
  const [managerOpen, setManagerOpen] = useState<number | null>(null);
  const selfReviews = useResource<SelfReviewSummaryRow[]>(`/people/self-reviews/employee/${employeeId}`);
  const managerReviews = useResource<SelfReviewSummaryRow[]>(
    `/people/self-reviews/employee/${employeeId}?kind=manager&shared=1`,
  );

  const list = reviews.data ?? [];
  const managerList = managerReviews.data ?? [];

  return (
    <>
      <div className="row-between wrap mb-4">
        <p className="muted small" style={{ maxWidth: '62ch' }}>
          Reviews of {person.FirstName} are written on the Employees page, beside their Hire Date, and
          appear here once they are shared.
        </p>
        <button type="button" className="btn btn-secondary" onClick={() => setSelfOpen('new')}>
          New self review
        </button>
      </div>

      {(reviews.loading || managerReviews.loading) && <Loading label="Loading reviews" />}
      {reviews.error && <ErrorBanner message={reviews.error} />}
      {managerReviews.error && <ErrorBanner message={managerReviews.error} />}

      {!reviews.loading && !managerReviews.loading && list.length === 0 && managerList.length === 0 && (
        <Empty title="No shared reviews yet">
          <p className="muted">
            Write one on the Employees page: open {person.FirstName}, then New review beside Hire Date.
            It stays private there until you share it.
          </p>
        </Empty>
      )}

      {managerList.length > 0 && (
        <div className="table-wrap card">
          <table className="table">
            <thead>
              <tr>
                <th>Review period</th>
                <th>Supervisor/Reviewer</th>
                <th className="num">Rated</th>
                <th>Overall rating</th>
                <th>Shared</th>
                <th className="actions" />
              </tr>
            </thead>
            <tbody>
              {managerList.map((m) => (
                <tr key={m.SelfReviewId}>
                  <td className="strong">
                    {m.PeriodFrom || m.PeriodTo
                      ? `${formatDate(m.PeriodFrom)} – ${formatDate(m.PeriodTo)}`
                      : `Started ${formatDate(m.CreatedUtc)}`}
                  </td>
                  <td>{m.ReviewerName ?? '—'}</td>
                  <td className="num">
                    {m.RatedCount} / {m.ItemCount}
                  </td>
                  <td>{m.OverallRating ? SELF_RATING_LABELS[m.OverallRating] : '—'}</td>
                  <td>{formatDate(m.SharedUtc)}</td>
                  <td className="actions">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setManagerOpen(m.SelfReviewId)}
                    >
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {list.length > 0 && (
        <section className="mt-6">
          <h3>Earlier reviews</h3>
          <p className="muted small mb-4">Built from People Templates, before the Manager Assessment Form.</p>
        <div className="table-wrap card">
          <table className="table">
            <thead>
              <tr>
                <th>Review</th>
                <th>Date</th>
                <th>Reviewer</th>
                <th className="num">Answered</th>
                <th>Status</th>
                <th className="actions" />
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.ReviewId}>
                  <td className="strong">{r.TemplateName ?? 'Blank review'}</td>
                  <td>{formatDate(r.ReviewDate)}</td>
                  <td>{r.ReviewerName ?? '—'}</td>
                  <td className="num">
                    {r.AnsweredCount} / {r.QuestionCount}
                  </td>
                  <td>
                    <span className={`badge ${r.Status === 'complete' ? 'badge-good' : 'badge-neutral'}`}>
                      {r.Status === 'complete' ? 'Complete' : 'Draft'}
                    </span>
                  </td>
                  <td className="actions">
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => onOpen(r.ReviewId)}>
                      Open
                    </button>
                    <DeleteButton
                      what="this review and its answers"
                      onConfirm={() => {
                        void api.del(`/people/reviews/${r.ReviewId}`).then(onChanged);
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </section>
      )}

      {managerOpen !== null && (
        <SelfReviewDialog
          kind="manager"
          readOnly
          employeeId={employeeId}
          employeeName={person.FullName}
          selfReviewId={managerOpen}
          defaults={{ jobTitle: person.RoleTitle, reviewerEmployeeId: person.ManagerEmployeeId }}
          employees={employees}
          onClose={() => setManagerOpen(null)}
          onSaved={() => setManagerOpen(null)}
        />
      )}

      {selfReviews.error && <ErrorBanner message={selfReviews.error} />}
      {(selfReviews.data ?? []).length > 0 && (
        <section className="mt-6">
          <h3>Self reviews</h3>
          <p className="muted small mb-4">
            The Employee Self-Assessment Form, filled in by {person.FullName} ahead of their review.
          </p>
          <div className="table-wrap card">
            <table className="table">
              <thead>
                <tr>
                  <th>Review period</th>
                  <th>Supervisor/Reviewer</th>
                  <th className="num">Rated</th>
                  <th>Overall self-rating</th>
                  <th>Signed</th>
                  <th className="actions" />
                </tr>
              </thead>
              <tbody>
                {(selfReviews.data ?? []).map((s) => (
                  <tr key={s.SelfReviewId}>
                    <td className="strong">
                      {s.PeriodFrom || s.PeriodTo
                        ? `${formatDate(s.PeriodFrom)} – ${formatDate(s.PeriodTo)}`
                        : `Started ${formatDate(s.CreatedUtc)}`}
                    </td>
                    <td>{s.ReviewerName ?? '—'}</td>
                    <td className="num">
                      {s.RatedCount} / {s.ItemCount}
                    </td>
                    <td>{s.OverallRating ? SELF_RATING_LABELS[s.OverallRating] : '—'}</td>
                    <td>
                      <span className={`badge ${s.EmployeeSignature ? 'badge-good' : 'badge-neutral'}`}>
                        {s.EmployeeSignature
                          ? s.ReviewerSignature
                            ? 'Both signed'
                            : 'Employee signed'
                          : 'Not signed'}
                      </span>
                    </td>
                    <td className="actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setSelfOpen(s.SelfReviewId)}
                      >
                        Open
                      </button>
                      <DeleteButton
                        what="this self review"
                        onConfirm={() => {
                          void api.del(`/people/self-reviews/${s.SelfReviewId}`).then(() => selfReviews.reload());
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {selfOpen !== null && (
        <SelfReviewDialog
          employeeId={employeeId}
          employeeName={person.FullName}
          selfReviewId={selfOpen === 'new' ? null : selfOpen}
          defaults={{ jobTitle: person.RoleTitle, reviewerEmployeeId: person.ManagerEmployeeId }}
          employees={employees}
          onClose={() => setSelfOpen(null)}
          onSaved={() => {
            setSelfOpen(null);
            selfReviews.reload();
          }}
        />
      )}

    </>
  );
}

function ReviewEditor({
  reviewId,
  firstName,
  employees,
  onShareChanged,
  onBack,
}: {
  reviewId: number;
  firstName: string;
  employees: Employee[];
  onShareChanged: () => void;
  onBack: () => void;
}) {
  const review = useResource<PeopleReview>(`/people/reviews/${reviewId}`);
  const [error, setError] = useState<string | null>(null);

  if (review.loading) return <Loading label="Loading the review" />;
  if (review.error) return <ErrorBanner message={review.error} />;
  const r = review.data;
  if (!r) return null;

  const readOnly = r.Status === 'complete';
  const answered = r.answers.filter(isAnswered).length;

  /* Grouped by section, preserving the order the answers were seeded in — that
     order IS the template's order, frozen at creation. */
  const sections: { title: string; answers: ReviewAnswer[] }[] = [];
  for (const a of r.answers) {
    const title = a.SectionTitle ?? 'General';
    const existing = sections.find((s) => s.title === title);
    if (existing) existing.answers.push(a);
    else sections.push({ title, answers: [a] });
  }

  const save = (patch: Record<string, unknown>) => {
    setError(null);
    void api
      .put(`/people/reviews/${reviewId}`, patch)
      .then(() => {
        review.reload();
        if ('shared' in patch) onShareChanged();
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not save'));
  };

  const isShared = r.SharedUtc !== null;

  return (
    <>
      <button type="button" className="btn btn-ghost btn-sm mb-4" onClick={onBack}>
        ← Back
      </button>

      {error && <ErrorBanner message={error} />}

      <section className="card mb-4">
        <div className="card-head">
          <div className="grow">
            <h3>{r.TemplateName ?? 'Blank review'}</h3>
            <div className="sub">
              {answered} of {r.answers.length} answered
            </div>
          </div>
          <div className="row">
            <span className={`badge ${isShared ? 'badge-good' : 'badge-warning'}`}>
              {isShared ? `Shared ${formatDate(r.SharedUtc)}` : 'Private'}
            </span>
            {isShared ? (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => save({ shared: false })}>
                Make private
              </button>
            ) : (
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => save({ shared: true })}>
                Share with {firstName}
              </button>
            )}
            <span className={`badge ${readOnly ? 'badge-good' : 'badge-neutral'}`}>
              {readOnly ? 'Complete' : 'Draft'}
            </span>
            {readOnly ? (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => save({ status: 'draft' })}>
                Reopen
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => save({ status: 'complete' })}
              >
                Mark complete
              </button>
            )}
          </div>
        </div>
        <div className="card-body">
          <div className="form-grid">
            <Field label="Review date">
              <input
                className="input"
                type="date"
                disabled={readOnly}
                defaultValue={r.ReviewDate?.slice(0, 10) ?? ''}
                onBlur={(e) => save({ reviewDate: e.target.value || null })}
              />
            </Field>
            <Field label="Reviewer">
              <select
                className="select"
                disabled={readOnly}
                value={r.ReviewerEmployeeId ?? ''}
                onChange={(e) =>
                  save({ reviewerEmployeeId: e.target.value ? Number(e.target.value) : null })
                }
              >
                <option value="">Unassigned</option>
                {employees.map((emp) => (
                  <option key={emp.EmployeeId} value={emp.EmployeeId}>
                    {emp.FullName}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Overall notes">
            <textarea
              className="textarea"
              rows={3}
              disabled={readOnly}
              defaultValue={r.Notes ?? ''}
              onBlur={(e) => save({ notes: e.target.value || null })}
              placeholder="Summary notes, overall impressions…"
            />
          </Field>
          {readOnly && (
            <p className="xs subtle">
              A complete review is read-only. Reopen it to change an answer — the API refuses the
              edit otherwise, so a finished review stays a record.
            </p>
          )}
        </div>
      </section>

      {sections.length === 0 ? (
        <Empty title="No questions in this review">
          <p className="muted">It was created without a template.</p>
        </Empty>
      ) : (
        <div className="stack">
          {sections.map((section) => (
            <section key={section.title} className="card">
              <div className="card-head">
                <div className="grow">
                  <h3>{section.title}</h3>
                </div>
                <span className="xs muted">
                  {section.answers.filter(isAnswered).length} / {section.answers.length}
                </span>
              </div>
              <div className="card-body">
                {section.answers.map((a) => (
                  <AnswerRow key={a.AnswerId} answer={a} readOnly={readOnly} onSaved={review.reload} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </>
  );
}

const isAnswered = (a: ReviewAnswer) =>
  a.Rating !== null || a.YesNoResponse !== null || (a.TextResponse ?? '').trim().length > 0;

function AnswerRow({
  answer,
  readOnly,
  onSaved,
}: {
  answer: ReviewAnswer;
  readOnly: boolean;
  onSaved: () => void;
}) {
  const [error, setError] = useState<string | null>(null);

  const save = (patch: Record<string, unknown>) => {
    setError(null);
    void api
      .put(`/people/review-answers/${answer.AnswerId}`, {
        rating: answer.Rating,
        textResponse: answer.TextResponse,
        yesNoResponse: answer.YesNoResponse,
        ...patch,
      })
      .then(onSaved)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not save'));
  };

  /* Anything that is SCORED sits in a fixed column down the right-hand edge;
     anything WRITTEN gets the full width under the question.

     The form this template came from is a three-column table — category,
     rating, comments — and the point of that shape is that the scores line up.
     A reviewer can run an eye down the right-hand edge and see the shape of a
     review without reading forty-four questions again. With the control simply
     following the text, every score landed at a different x depending on how
     long its question happened to be, which is the same information laid out
     so it cannot be read.

     Yes/no counts as scored: it is a two-state answer of fixed width, and
     leaving it inline would break the column at the one row that closes the
     review. Free text does not — a comment box squeezed into a 176px gutter
     is unusable, so it wraps beneath instead. */
  const scored = answer.ResponseType === 'rating' || answer.ResponseType === 'yesno';

  return (
    <div className={`mini-row answer-row${scored ? ' is-scored' : ''}`}>
      <div className="answer-q">
        <p className="small">{answer.QuestionText}</p>
        {error && <p className="xs flagged">{error}</p>}
      </div>

      {answer.ResponseType === 'rating' && (
        <div className="answer-score">
          {readOnly ? (
            <p className="mono">{answer.Rating ?? '—'} / 5</p>
          ) : (
            <ScaleInput value={answer.Rating} onChange={(rating) => save({ rating })} />
          )}
        </div>
      )}

      {answer.ResponseType === 'yesno' && (
        <div className="answer-score">
          {readOnly ? (
            <p className="small">
              {answer.YesNoResponse === null ? '—' : answer.YesNoResponse ? 'Yes' : 'No'}
            </p>
          ) : (
            <div className="status-group">
              {[
                { label: 'Yes', value: true },
                { label: 'No', value: false },
              ].map((opt) => (
                <button
                  key={opt.label}
                  type="button"
                  className={`status-btn${answer.YesNoResponse === opt.value ? ' met on' : ''}`}
                  onClick={() =>
                    save({ yesNoResponse: answer.YesNoResponse === opt.value ? null : opt.value })
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {answer.ResponseType === 'text' && (
        <div className="answer-written">
          {readOnly ? (
            <p className="small muted" style={{ whiteSpace: 'pre-wrap' }}>
              {answer.TextResponse || '—'}
            </p>
          ) : (
            <textarea
              className="textarea"
              rows={3}
              defaultValue={answer.TextResponse ?? ''}
              onBlur={(e) => {
                if ((answer.TextResponse ?? '') !== e.target.value) {
                  save({ textResponse: e.target.value || null });
                }
              }}
              placeholder="Enter your response…"
            />
          )}
        </div>
      )}
    </div>
  );
}

/* =========================================================================
   Training
   ========================================================================= */

function TrainingTab({
  employeeId,
  hasRoleTraining,
}: {
  employeeId: number;
  hasRoleTraining: boolean;
}) {
  const training = useResource<EmployeeTraining>(`/people/training/employee/${employeeId}`);

  /* The tab shows TWO things, and either can be empty on its own: the
     training the role carries, and the training this person was personally
     approved for on the Training Request page (migration 187). A role with
     no matrix used to return early here, which would now hide somebody's
     approved courses because of a list nobody has built yet. */
  if (training.loading) return <Loading label="Loading training" />;
  if (training.error) return <ErrorBanner message={training.error} />;

  const data = training.data;
  const approved = data?.requests ?? [];
  const items = data?.items ?? [];
  const required = items.filter((i) => i.Category === 'required');
  const optional = items.filter((i) => i.Category === 'optional');
  const done = required.filter((i) => i.CompletedOn).length;

  if (items.length === 0 && approved.length === 0) {
    return (
      <Empty title={hasRoleTraining ? 'No training defined for this role yet' : 'No training for this role'}>
        <p className="muted">
          {hasRoleTraining
            ? 'Add it on the Training tab, and it appears here for everyone in the role.'
            : "Training follows the job role somebody holds in the directory, and this person's role has no training list yet. Set their role on the Employees page, or build the list for it on the Training tab."}
        </p>
        <p className="muted">
          Approved training requests appear here too — raise one on the Training Request page.
        </p>
      </Empty>
    );
  }

  return (
    <>
      {items.length > 0 && data?.role && (
        <div className="row-between wrap mb-4">
          <p className="muted small">
            Training for <strong>{data.role.Name}</strong>. The list is edited on the Training tab —
            here you record who has done what.
          </p>
          <span className={`badge ${required.length > 0 && done === required.length ? 'badge-good' : 'badge-warning'}`}>
            {done} / {required.length} required complete
          </span>
        </div>
      )}

      <div className="stack">
        {required.length > 0 && (
          <CompletionList
            title="Required training"
            items={required}
            employeeId={employeeId}
            onChanged={training.reload}
          />
        )}
        {optional.length > 0 && (
          <CompletionList
            title="Optional training"
            items={optional}
            employeeId={employeeId}
            onChanged={training.reload}
          />
        )}
        {approved.length > 0 && (
          <ApprovedRequests requests={approved} onChanged={training.reload} />
        )}
      </div>
    </>
  );
}

/**
 * Training this person was approved for by name — their own courses, not the
 * role's list. Each one arrived through the Training Request page and was
 * approved by their supervisor; this is where somebody records that it was
 * actually done.
 *
 * Denied and pending requests are NOT here. A profile is the record of what
 * was agreed, and a list that mixed "approved" with "asked for and refused"
 * would be read wrongly by whoever opens it in a year's time.
 */
function ApprovedRequests({
  requests,
  onChanged,
}: {
  requests: ApprovedTrainingRequest[];
  onChanged: () => void;
}) {
  const set = (id: number, completedOn: string | null) => {
    void api.put(`/training-requests/${id}/completion`, { completedOn }).then(onChanged);
  };

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h3>Approved training requests</h3>
          <p className="xs muted">
            Approved on the Training Request page. Tick one off when it has been completed.
          </p>
        </div>
      </div>
      <div className="card-body">
        {requests.map((r) => (
          <div key={r.TrainingRequestId} className="mini-row row-between wrap">
            <label className="checkbox grow">
              <input
                type="checkbox"
                checked={r.CompletedOn !== null}
                onChange={() => set(r.TrainingRequestId, r.CompletedOn ? null : todayLocal())}
              />
              <span className="strong">{r.Program}</span>
              <span className="xs muted">
                {r.Provider ? `${r.Provider} · ` : ''}
                {r.ApprovedAmount !== null
                  ? `up to ${formatNumber(Number(r.ApprovedAmount), 'currency')}`
                  : 'no amount set'}
                {r.ApprovedHours !== null ? ` · ${Number(r.ApprovedHours)} paid hours` : ''}
                {r.DecidedByName ? ` · approved by ${r.DecidedByName}` : ''}
                {r.DecidedUtc ? ` on ${formatDate(r.DecidedUtc)}` : ''}
              </span>
            </label>
            {r.CompletedOn ? (
              <input
                className="input"
                type="date"
                style={{ maxWidth: 170 }}
                value={r.CompletedOn.slice(0, 10)}
                onChange={(e) => set(r.TrainingRequestId, e.target.value || null)}
                aria-label={`Completion date for ${r.Program}`}
              />
            ) : (
              <span className="xs subtle">Not completed</span>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function CompletionList({
  title,
  items,
  employeeId,
  onChanged,
}: {
  title: string;
  items: EmployeeTraining['items'];
  employeeId: number;
  onChanged: () => void;
}) {
  const set = (itemId: number, completedOn: string | null) => {
    void api
      .put(`/people/training/employee/${employeeId}/items/${itemId}`, { completedOn })
      .then(onChanged);
  };

  return (
    <section className="card">
      <div className="card-head">
        <h3>{title}</h3>
      </div>
      <div className="card-body">
        {items.map((item) => (
          <div key={item.TrainingItemId} className="mini-row row-between wrap">
            <label className="checkbox grow">
              <input
                type="checkbox"
                checked={item.CompletedOn !== null}
                onChange={() => set(item.TrainingItemId, item.CompletedOn ? null : todayLocal())}
              />
              <span className="strong">{item.Title}</span>
              {item.Link && (
                <a className="xs" href={item.Link} target="_blank" rel="noopener noreferrer">
                  open ↗
                </a>
              )}
            </label>
            {item.CompletedOn ? (
              <input
                className="input"
                type="date"
                style={{ maxWidth: 170 }}
                value={item.CompletedOn.slice(0, 10)}
                onChange={(e) => set(item.TrainingItemId, e.target.value || null)}
                aria-label={`Completion date for ${item.Title}`}
              />
            ) : (
              <span className="xs subtle">Not completed</span>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/* =========================================================================
   Profile
   ========================================================================= */

function ProfileTab({
  person,
  hireDate,
  onSaved,
}: {
  person: PersonDetail;
  /** Already formatted: Autotask's Resources.hireDate, "—" when there is none. */
  hireDate: string;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEdit = () => {
    setForm(
      Object.fromEntries(
        ALL_PROFILE_FIELDS.map((f) => [f.key, (person[f.key] as string | null) ?? '']),
      ),
    );
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/people/directory/${person.EmployeeId}/profile`, form);
      onSaved();
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="row-between wrap mb-4">
        <p className="muted small" style={{ maxWidth: '60ch' }}>
          Who this person is beyond the org chart. Some of it is personal — it is here so a 1:1 is a
          conversation rather than a status update, not so it can be reported on.
        </p>
        {editing ? (
          <div className="row">
            <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => setEditing(false)}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void save()}>
              {saving ? 'Saving…' : 'Save profile'}
            </button>
          </div>
        ) : (
          <button type="button" className="btn btn-ghost" onClick={startEdit}>
            Edit profile
          </button>
        )}
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="stack">
        {PROFILE_SECTIONS.map((section) => (
          <section key={section.title} className="card">
            <div className="card-head">
              <h3>{section.title}</h3>
            </div>
            <div className="card-body">
              <div className="form-grid">
                {section.fields.map((f) => {
                  const stored = (person[f.key] as string | null) ?? '';
                  return (
                    <div
                      key={f.key}
                      className="field"
                      style={f.type === 'textarea' ? { gridColumn: '1 / -1' } : undefined}
                    >
                      <label>{f.label}</label>
                      {editing ? (
                        f.type === 'textarea' ? (
                          <textarea
                            className="textarea"
                            rows={3}
                            value={form[f.key] ?? ''}
                            placeholder={f.hint}
                            onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                          />
                        ) : f.type === 'select' ? (
                          <select
                            className="select"
                            value={form[f.key] ?? ''}
                            onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                          >
                            {(f.options ?? []).map((o) => (
                              <option key={o} value={o}>
                                {o || 'Not set'}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            className="input"
                            type={f.type === 'date' ? 'date' : 'text'}
                            value={form[f.key] ?? ''}
                            placeholder={f.type === 'date' ? undefined : f.hint}
                            onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                          />
                        )
                      ) : stored ? (
                        /* A DATE arrives from the driver as a full ISO timestamp,
                           so it is trimmed for display — the stored value is only
                           ever the day. */
                        f.type === 'date' ? (
                          <p className="small">{formatDate(stored)}</p>
                        ) : (
                          <p className="small" style={{ whiteSpace: 'pre-wrap' }}>
                            {stored}
                          </p>
                        )
                      ) : (
                        <p className="small subtle">{f.hint ?? '—'}</p>
                      )}
                    </div>
                  );
                })}
                {/* Not one of the profile's own fields: it is Autotask's, so it
                    is shown in Basics but never edited here. */}
                {section.title === 'Basics' && (
                  <div className="field">
                    <label>Hire Date</label>
                    <p className="small">{hireDate}</p>
                    {editing && <p className="xs subtle">From Autotask; changed there.</p>}
                  </div>
                )}
              </div>
            </div>
          </section>
        ))}
      </div>
    </>
  );
}
